import React, { useState, useMemo, useEffect } from 'react';
import userMetadataApi from '../../../api/userMetadataApi';
import vendorApi from '../../../api/vendorApi';
import { useQuery } from '@tanstack/react-query';
import { Industry } from '../../../api/types';
import { useWorkspace } from '../../../context/WorkspaceContext';

/** Restaurant type values matching backend restaurant_seasonal_defaults */
const RESTAURANT_TYPE_OPTIONS = [
    { value: 'full_service', label: 'Full Service', description: 'Table service, waitstaff' },
    { value: 'quick_service', label: 'Quick Service', description: 'Fast food, counter service' },
    { value: 'catering', label: 'Catering', description: 'Off-premise events & catering' },
    { value: 'food_truck', label: 'Food Truck', description: 'Mobile food service' },
    { value: 'bakery_cafe', label: 'Bakery / Cafe', description: 'Bakery, coffee shop, cafe' },
    { value: 'other', label: 'Other / Not a restaurant', description: 'Other food service or non-restaurant business' },
] as const;

/** Restaurant size values matching backend */
const RESTAURANT_SIZE_OPTIONS = [
    { value: 'small', label: 'Small', description: '1–2 locations' },
    { value: 'medium', label: 'Medium', description: '3–10 locations' },
    { value: 'large', label: 'Large', description: '11+ locations' },
] as const;

interface IndustrySelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Pre-filled values from active business; used to show only missing sections */
    initialValues?: {
        industry?: string;
        business_type?: string;
        naics_code?: string;
        restaurant_type?: string;
        restaurant_size?: string;
        average_seatings_per_location?: number;
    };
}

interface SelectableOption {
    industry: string;
    business_type: string;
    naics_code: string;
}

function isRestaurantProfile(option?: Partial<SelectableOption> | null): boolean {
    if (!option) return false;
    const industry = (option.industry || '').toLowerCase();
    const businessType = (option.business_type || '').toLowerCase();
    const naicsCode = String(option.naics_code || '');
    return (
        industry === 'restaurant' ||
        naicsCode.startsWith('722') ||
        /restaurant|cater|food truck|bakery|cafe|coffee shop/.test(businessType)
    );
}

function inferRestaurantType(option?: Partial<SelectableOption> | null): string {
    if (!option) return '';
    const businessType = (option.business_type || '').toLowerCase();
    const naicsCode = String(option.naics_code || '');

    if (naicsCode === '722511' || businessType.includes('full-service')) return 'full_service';
    if (naicsCode === '722513' || businessType.includes('quick-service') || businessType.includes('fast food')) return 'quick_service';
    if (naicsCode === '722320' || businessType.includes('cater')) return 'catering';
    if (naicsCode === '722330' || businessType.includes('food truck')) return 'food_truck';
    if (businessType.includes('bakery') || businessType.includes('cafe') || businessType.includes('coffee')) return 'bakery_cafe';
    return '';
}

function getRestaurantTypeLabel(value: string): string {
    return RESTAURANT_TYPE_OPTIONS.find((opt) => opt.value === value)?.label || value;
}

export default function IndustrySelectionModal({
    isOpen,
    onClose,
    onSuccess,
    initialValues,
}: IndustrySelectionModalProps) {
    const { activeBusinessId } = useWorkspace();
    const [selectedOption, setSelectedOption] = useState<SelectableOption | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [restaurantType, setRestaurantType] = useState<string>('');
    const [restaurantSize, setRestaurantSize] = useState<string>('');
    const [averageSeatings, setAverageSeatings] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState<'industry' | 'restaurant'>('industry');

    const hasIndustry = !!(initialValues?.industry && initialValues?.business_type && initialValues?.naics_code);
    const initialBusinessOption = useMemo(
        () => (
            initialValues?.industry && initialValues?.business_type && initialValues?.naics_code
                ? {
                    industry: initialValues.industry,
                    business_type: initialValues.business_type,
                    naics_code: initialValues.naics_code,
                }
                : null
        ),
        [initialValues]
    );
    const restaurantSourceOption = selectedOption || initialBusinessOption;
    const isRestaurantBusiness = useMemo(
        () => isRestaurantProfile(restaurantSourceOption),
        [restaurantSourceOption]
    );
    const inferredRestaurantType = useMemo(
        () => inferRestaurantType(restaurantSourceOption),
        [restaurantSourceOption]
    );
    const effectiveRestaurantType = restaurantType || initialValues?.restaurant_type || inferredRestaurantType;
    const hasRestaurantType = !!effectiveRestaurantType;
    const hasRestaurantSize = !!initialValues?.restaurant_size;

    const showIndustrySection = !hasIndustry;
    const showRestaurantSection = isRestaurantBusiness && (!hasRestaurantType || !hasRestaurantSize);
    const needsRestaurantTypeSelection = showRestaurantSection && !initialValues?.restaurant_type && !inferredRestaurantType;
    const needsRestaurantSizeSelection = showRestaurantSection && !initialValues?.restaurant_size;
    const selectedOptionIsRestaurant = isRestaurantProfile(selectedOption);

    // Sync from initialValues when modal opens
    useEffect(() => {
        if (!isOpen) return;
        if (hasIndustry && initialValues?.industry && initialValues?.business_type && initialValues?.naics_code) {
            setSelectedOption({
                industry: initialValues.industry,
                business_type: initialValues.business_type,
                naics_code: initialValues.naics_code,
            });
        } else {
            setSelectedOption(null);
        }
        setRestaurantType(initialValues?.restaurant_type || inferRestaurantType({
            industry: initialValues?.industry,
            business_type: initialValues?.business_type,
            naics_code: initialValues?.naics_code,
        }));
        setRestaurantSize(initialValues?.restaurant_size || '');
        setAverageSeatings(
            initialValues?.average_seatings_per_location != null
                ? String(initialValues.average_seatings_per_location)
                : ''
        );
    }, [isOpen, initialValues, hasIndustry]);

    useEffect(() => {
        if (!isOpen) return;
        const initialIsRestaurant = isRestaurantProfile(initialBusinessOption);
        const initialHasRestaurantType = !!(initialValues?.restaurant_type || inferRestaurantType(initialBusinessOption));
        const initialHasRestaurantSize = !!initialValues?.restaurant_size;
        if (hasIndustry && initialIsRestaurant && (!initialHasRestaurantType || !initialHasRestaurantSize)) {
            setCurrentStep('restaurant');
            return;
        }
        setCurrentStep('industry');
    }, [isOpen, hasIndustry, initialBusinessOption, initialValues]);

    const { data: industries, isLoading: isLoadingIndustries } = useQuery({
        queryKey: ['industries'],
        queryFn: vendorApi.getIndustries,
        enabled: isOpen,
    });

    const allOptions = useMemo(() => {
        if (!industries) return [];

        const options: SelectableOption[] = [];
        const seenNaics = new Set<string>();

        industries.forEach((ind: Industry) => {
            if (ind.business_types) {
                ind.business_types.forEach((bt) => {
                    const naicsCode = String(bt.naics_code || '').trim();
                    if (!naicsCode) {
                        return;
                    }
                    if (!seenNaics.has(naicsCode)) {
                        seenNaics.add(naicsCode);
                        options.push({
                            industry: ind.industry,
                            business_type: bt.name,
                            naics_code: naicsCode,
                        });
                    }
                });
            }
        });
        return options;
    }, [industries]);

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return allOptions;
        const query = searchQuery.toLowerCase();
        return allOptions.filter(
            (opt) =>
                opt.industry.toLowerCase().includes(query) ||
                opt.business_type.toLowerCase().includes(query) ||
                opt.naics_code.toLowerCase().includes(query)
        );
    }, [allOptions, searchQuery]);

    const industryValid = !showIndustrySection || selectedOption != null;
    const restaurantValid =
        !showRestaurantSection ||
        ((!needsRestaurantTypeSelection || !!effectiveRestaurantType) &&
            (!needsRestaurantSizeSelection || !!restaurantSize));
    const canSave = industryValid && restaurantValid;

    const handleContinue = async () => {
        if (isLoading) return;
        if (currentStep === 'industry') {
            if (!industryValid) return;
            if (showIndustrySection && selectedOption && isRestaurantProfile(selectedOption)) {
                setCurrentStep('restaurant');
                return;
            }
            await handleSave();
        } else {
            await handleSave();
        }
    };

    const handleSave = async () => {
        if (!canSave) return;

        setIsLoading(true);
        try {
            const updatePayload: {
                industry?: string;
                business_type?: string;
                naics_code?: string;
                restaurant_type?: string;
                restaurant_size?: string;
                average_seatings_per_location?: number;
            } = {};

            if (showIndustrySection && selectedOption) {
                updatePayload.industry = selectedOption.industry;
                updatePayload.business_type = selectedOption.business_type;
                updatePayload.naics_code = selectedOption.naics_code;
            }

            if (showRestaurantSection && (currentStep === 'restaurant' || !showIndustrySection)) {
                const nextRestaurantType = restaurantType || inferredRestaurantType || initialValues?.restaurant_type;
                const nextRestaurantSize = restaurantSize || initialValues?.restaurant_size;
                if (nextRestaurantType) {
                    updatePayload.restaurant_type = nextRestaurantType;
                }
                if (nextRestaurantSize) {
                    updatePayload.restaurant_size = nextRestaurantSize;
                }
                const seatingsNum = parseInt(averageSeatings, 10);
                if (!Number.isNaN(seatingsNum) && seatingsNum > 0) {
                    updatePayload.average_seatings_per_location = seatingsNum;
                }
            }

            if (Object.keys(updatePayload).length === 0) {
                onClose();
                return;
            }

            await userMetadataApi.updateUserMetadata(updatePayload, activeBusinessId || undefined);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to update business profile', error);
            alert('Failed to save. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white w-full max-w-3xl relative flex flex-col shadow-2xl rounded-2xl overflow-hidden max-h-[94vh] min-h-[760px]">
                <div className="p-6 pb-0">
                    <h3 className="text-2xl font-bold leading-6 text-gray-900 text-center mb-2">
                        Complete Your Business Profile
                    </h3>
                    <p className="text-sm text-gray-500 text-center mb-6">
                        These details help us provide better forecasts and insights.
                    </p>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-2 space-y-6">
                    {showIndustrySection && currentStep === 'industry' && (
                        <section className="flex min-h-[360px] flex-col">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                Industry & Business Type
                            </h4>
                            <p className="text-xs text-gray-500 mb-3">
                                Search for your industry, business type, or NAICS code.
                            </p>
                            <div className="relative mb-4">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg
                                        className="h-5 w-5 text-gray-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search Industry, Business Type, or NAICS..."
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="grid flex-1 auto-rows-fr grid-cols-1 gap-2 overflow-y-auto items-stretch pr-1">
                                {isLoadingIndustries ? (
                                    <div className="text-gray-500 py-4">Loading industries...</div>
                                ) : filteredOptions.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500">
                                        No matches found.
                                    </div>
                                ) : (
                                    filteredOptions.map((option) => {
                                        const isSelected = selectedOption?.naics_code === option.naics_code;
                                        return (
                                            <button
                                                key={option.naics_code}
                                                onClick={() => setSelectedOption(option)}
                                                className={`relative flex items-stretch min-h-[88px] p-3 cursor-pointer rounded-xl border transition-all duration-200 text-left ${
                                                    isSelected
                                                        ? 'border-indigo-600 bg-indigo-50/50 ring-1 ring-indigo-600'
                                                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                    <span
                                                        className={`font-semibold text-sm truncate block ${
                                                            isSelected ? 'text-indigo-900' : 'text-gray-900'
                                                        }`}
                                                    >
                                                        {option.business_type}
                                                    </span>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span
                                                            className={`text-xs px-2 py-0.5 rounded-full ${
                                                                isSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                                                            }`}
                                                        >
                                                            {option.industry}
                                                        </span>
                                                        <span className={`text-xs ${isSelected ? 'text-indigo-600' : 'text-gray-500'}`}>
                                                            NAICS: {option.naics_code}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isSelected && (
                                                    <div className="shrink-0 ml-3 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                                                        <div className="w-2 h-2 rounded-full bg-white" />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </section>
                    )}

                    {showRestaurantSection && currentStep === 'restaurant' && (
                        <section className="border-t border-gray-100 pt-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-1">
                                Restaurant Profile
                            </h4>
                            <p className="text-xs text-gray-500 mb-3">
                                We only ask for these details for restaurant businesses because they improve forecast defaults.
                            </p>

                            <div className="space-y-4">
                                {!needsRestaurantTypeSelection && effectiveRestaurantType && (
                                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                                        <div className="text-xs font-medium text-emerald-800">Restaurant Type</div>
                                        <div className="text-sm text-emerald-900">
                                            {getRestaurantTypeLabel(effectiveRestaurantType)}
                                        </div>
                                        <div className="text-xs text-emerald-700 mt-1">
                                            Inferred from your selected business type.
                                        </div>
                                    </div>
                                )}

                                {needsRestaurantTypeSelection && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                            Restaurant Type
                                        </label>
                                        <div className="grid grid-cols-2 gap-2 items-stretch">
                                            {RESTAURANT_TYPE_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => setRestaurantType(opt.value)}
                                                    className={`h-full min-h-[92px] p-3 rounded-lg border text-left transition-all ${
                                                        effectiveRestaurantType === opt.value
                                                            ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                                                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span
                                                        className={`block font-medium text-sm ${
                                                            effectiveRestaurantType === opt.value ? 'text-indigo-900' : 'text-gray-900'
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </span>
                                                    <span className="block text-xs text-gray-500 mt-0.5">
                                                        {opt.description}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                        Business Size (locations)
                                    </label>
                                        <div className="grid grid-cols-3 gap-2 items-stretch">
                                            {RESTAURANT_SIZE_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => setRestaurantSize(opt.value)}
                                                    className={`h-full min-h-[88px] p-3 rounded-lg border text-center transition-all ${
                                                        restaurantSize === opt.value
                                                            ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600'
                                                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <span
                                                    className={`block font-medium text-sm ${
                                                        restaurantSize === opt.value ? 'text-indigo-900' : 'text-gray-900'
                                                    }`}
                                                >
                                                    {opt.label}
                                                </span>
                                                <span className="block text-xs text-gray-500 mt-0.5">
                                                    {opt.description}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {effectiveRestaurantType && effectiveRestaurantType !== 'other' && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                            Average seatings per location <span className="text-gray-400">(optional)</span>
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={9999}
                                            placeholder="e.g. 50"
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                            value={averageSeatings}
                                            onChange={(e) => setAverageSeatings(e.target.value)}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Helps improve forecast accuracy for restaurants with known capacity.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>

                <div className="p-6 pt-4 border-t border-gray-100 bg-white">
                    <div className="flex gap-3">
                        {currentStep === 'restaurant' && showIndustrySection && (
                            <button
                                type="button"
                                onClick={() => setCurrentStep('industry')}
                                disabled={isLoading}
                                className="flex-1 inline-flex justify-center rounded-lg border border-gray-300 px-4 py-3 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleContinue}
                            disabled={
                                isLoading ||
                                (currentStep === 'industry' && !industryValid) ||
                                (currentStep === 'restaurant' && !restaurantValid)
                            }
                            className={`${
                                currentStep === 'restaurant' && showIndustrySection ? 'flex-1' : 'w-full'
                            } inline-flex justify-center rounded-lg border border-transparent px-4 py-3 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                isLoading ||
                                (currentStep === 'industry' && !industryValid) ||
                                (currentStep === 'restaurant' && !restaurantValid)
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                        >
                            {isLoading
                                ? 'Saving...'
                                : currentStep === 'industry'
                                    ? (showIndustrySection && selectedOptionIsRestaurant ? 'Next' : 'Save Profile')
                                    : 'Save Profile'}
                        </button>
                    </div>
                    <div className="mt-3 text-center">
                        <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
